import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { OrgMember } from '../types';
import type { MemberRole } from '../../../types/roles';

interface UseOrgMembersReturn {
  activeMembers: OrgMember[];
  deactivatedMembers: OrgMember[];
  inviteMember: (email: string, firstName: string, lastName: string, role: MemberRole) => Promise<'ok' | 'error'>;
  deactivateMember: (userId: string) => Promise<boolean>;
  reactivateMember: (userId: string) => Promise<boolean>;
  updateMemberRole: (userId: string, role: MemberRole) => Promise<boolean>;
}

function sortMembers(members: OrgMember[]): OrgMember[] {
  return [...members].sort((a, b) => {
    const nameA =
      [a.first_name, a.last_name].filter(Boolean).join(' ').toLowerCase() ||
      a.email?.toLowerCase() ||
      '';
    const nameB =
      [b.first_name, b.last_name].filter(Boolean).join(' ').toLowerCase() ||
      b.email?.toLowerCase() ||
      '';
    return nameA.localeCompare(nameB, 'nl');
  });
}

export function useOrgMembers(orgId: string | null): UseOrgMembersReturn {
  const [members, setMembers] = useState<OrgMember[]>([]);

  const loadTeam = async () => {
    if (!orgId) { setMembers([]); return; }
    try {
      const { data } = await supabase.rpc('get_org_members', { org_id: orgId });
      setMembers(sortMembers(data ?? []));
    } catch {
      setMembers([]);
    }
  };

  useEffect(() => {
    loadTeam();
  }, [orgId]);

  const inviteMember = async (
    email: string,
    firstName: string,
    lastName: string,
    role: MemberRole,
  ): Promise<'ok' | 'error'> => {
    if (!orgId) return 'error';
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return 'error';
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: email.trim(),
          organization_id: orgId,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          role,
        }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      await loadTeam();
      return 'ok';
    } catch {
      return 'error';
    }
  };

  const deactivateMember = async (userId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ active: false })
        .eq('user_id', userId);
      if (error) throw error;
      setMembers((prev) => prev.map((m) => (m.user_id === userId ? { ...m, active: false } : m)));
      return true;
    } catch {
      return false;
    }
  };

  const reactivateMember = async (userId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ active: true })
        .eq('user_id', userId);
      if (error) throw error;
      setMembers((prev) => prev.map((m) => (m.user_id === userId ? { ...m, active: true } : m)));
      return true;
    } catch {
      return false;
    }
  };

  const updateMemberRole = async (userId: string, role: MemberRole): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role })
        .eq('user_id', userId);
      if (error) throw error;
      setMembers((prev) => prev.map((m) => (m.user_id === userId ? { ...m, role } : m)));
      return true;
    } catch {
      return false;
    }
  };

  return {
    activeMembers: members.filter((m) => m.active !== false),
    deactivatedMembers: members.filter((m) => m.active === false),
    inviteMember,
    deactivateMember,
    reactivateMember,
    updateMemberRole,
  };
}
